import type { components } from './generated/schema'
import {
  asNullableText,
  asText,
  FormatError,
  isRecord,
} from './parse'

type Schemas = components['schemas']

const FALLBACK_CATEGORY_COLOR = '#00645F'

function requireString(
  record: Record<string, unknown>,
  field: string,
  label: string,
): string {
  const value = record[field]
  if (typeof value !== 'string') {
    throw new FormatError(`Malformed ${label} record`)
  }
  return value
}

function activityTextFields(record: Record<string, unknown>) {
  // Keep the frozen projection shape while normalising the string fields the
  // list/card surfaces dereference. Optional values use the contract's null.
  return {
    categorySlug: asText(record.categorySlug),
    categoryName: asText(record.categoryName),
    categoryColor: asText(record.categoryColor) || FALLBACK_CATEGORY_COLOR,
    city: asNullableText(record.city),
    locationName: asNullableText(record.locationName),
    coverImageUrl: asNullableText(record.coverImageUrl),
    meetingPoint: asNullableText(record.meetingPoint),
    priceDescription: asNullableText(record.priceDescription),
    inviteToken: asNullableText(record.inviteToken),
  }
}

/**
 * Runtime guard for discovery/agenda/history items. Only the record identity
 * is required; legacy fields that the API already allows to be null keep their
 * defaults, and unrelated valid fields are preserved untouched.
 */
export function parseActivityProjection(
  value: unknown,
): Schemas['ActivityProjection'] {
  if (!isRecord(value)) throw new FormatError('Malformed activity record')

  const id = requireString(value, 'id', 'activity')
  if (!id.trim()) throw new FormatError('Malformed activity record')
  const title = requireString(value, 'title', 'activity')

  return {
    ...value,
    id,
    title,
    ...activityTextFields(value),
  } as unknown as Schemas['ActivityProjection']
}

/** Saved activities share the projection identity but omit some fields. */
export function parseSavedActivity(value: unknown): Schemas['SavedActivity'] {
  return parseActivityProjection(value) as unknown as Schemas['SavedActivity']
}

/**
 * Categories are also consumed by the activity composer. The slug drives the
 * cover lookup (it is trimmed), so a legacy missing slug becomes ''; `name`
 * and `id` are required by every category surface.
 */
export function parseCategory(value: unknown): Schemas['Category'] {
  if (!isRecord(value)) throw new FormatError('Malformed category record')

  const id = requireString(value, 'id', 'category')
  if (!id.trim()) throw new FormatError('Malformed category record')
  const name = requireString(value, 'name', 'category')

  return {
    ...value,
    id,
    name,
    slug: asText(value.slug),
    color: asText(value.color) || FALLBACK_CATEGORY_COLOR,
    icon: asNullableText(value.icon),
  } as unknown as Schemas['Category']
}
